import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { authAPI } from '../../services/authAPI';

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  twoFactorRequired: boolean;
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  twoFactorRequired: false,
};

export const login = createAsyncThunk(
  'auth/login',
  async (code: string) => {
    const response = await authAPI.githubCallback(code);
    return response.data;
  }
);

export const verify2FA = createAsyncThunk(
  'auth/verify2FA',
  async (code: string) => {
    const response = await authAPI.verify2FA(code);
    return response.data;
  }
);

export const refreshToken = createAsyncThunk(
  'auth/refresh',
  async () => {
    // Refresh token is sent via httpOnly cookie automatically.
    // No tokens are passed in JS; the server rotates cookies server-side.
    const response = await authAPI.refreshToken();
    return response.data;
  }
);

// logoutAsync calls the server to clear httpOnly cookies, then clears Redux state.
export const logoutAsync = createAsyncThunk(
  'auth/logoutAsync',
  async (_, { dispatch }) => {
    try {
      await authAPI.logout();
    } finally {
      // Always clear Redux state even if server call fails
      dispatch(authSlice.actions.logout());
    }
  }
);

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // SECURITY: logout clears Redux state only.
    // httpOnly cookies are cleared by calling authAPI.logout() via logoutAsync.
    // There is no localStorage to clean up — tokens were never stored there.
    logout: () => initialState,
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.twoFactorRequired) {
          state.twoFactorRequired = true;
        } else {
          state.isAuthenticated = true;
          // Map engineer object from server response to User shape
          const eng = action.payload.engineer;
          if (eng) {
            state.user = {
              id: eng.engineer_id ?? eng.id,
              email: eng.email,
              name: eng.name,
              role: eng.role,
            };
          } else if (action.payload.user) {
            state.user = action.payload.user;
          }
          // NOTE: tokens are NOT stored in localStorage.
          // They are delivered as httpOnly cookies by the server.
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Login failed';
      })
      .addCase(verify2FA.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.twoFactorRequired = false;
        const eng = action.payload.engineer;
        if (eng) {
          state.user = {
            id: eng.engineer_id ?? eng.id,
            email: eng.email,
            name: eng.name,
            role: eng.role,
          };
        } else if (action.payload.user) {
          state.user = action.payload.user ?? null;
        }
        // NOTE: tokens are NOT stored in localStorage.
      })
      .addCase(refreshToken.fulfilled, (_state, _action) => {
        // New access_token cookie is set by the server automatically.
        // No client-side token storage needed.
      })
      .addCase(refreshToken.rejected, () => {
        // Refresh failed — clear all auth state
        return initialState;
      });
  },
});

export const { logout, setUser } = authSlice.actions;
export default authSlice.reducer;
